import JSZip from "jszip";
import { ClusteredBase } from "./downloadMetadataBase";
import { buildStructuralColumns, ColumnStructural, escapeCSV, toExcelBuffer, toUTF8CSV } from "./helpers";
import { INDICATOR_TYPE_STRUCTURAL } from "../../../../utils/const";
import { saveAs } from "file-saver";
import { DataGraphicsResultAndProcess, DerechoDto, HeatMapData } from "../../../../interfaces/OpenData";

const headerDictionary = [
    'Código',
    'Indicador',
    'Descripción del indicador',
    'Fuente'
];

export const downloadIndicatorClusteredBase = async ({ indicators, periods, format = 'csv' }: ClusteredBase) => {
    if (!indicators.length) return;

    const isExcel = format === 'excel';
    const zip = new JSZip();

    const addToZip = async (name: string, rows: unknown[][]) => {
        if (isExcel) {
            zip.file(`${name}.xlsx`, await toExcelBuffer(rows));
        } else {
            zip.file(`${name}.csv`, toUTF8CSV(rows));
        }
    };

    const structuralIndicators = indicators.filter(ind => ind.tipo_indicador === INDICATOR_TYPE_STRUCTURAL);
    const processResultIndicators = indicators.filter(ind => ind.tipo_indicador !== INDICATOR_TYPE_STRUCTURAL);

    if (structuralIndicators.length > 0) {

        const allColumns: ColumnStructural[] = [];

        structuralIndicators.forEach(indicator => {
            if (indicator.data && Array.isArray(indicator.data) && indicator.data.length > 0) {
                const derechos = indicator.data as DerechoDto[];
                const columns = buildStructuralColumns(derechos);

                columns.forEach(col => {
                    const exists = allColumns.some(c => c.derecho === col.derecho && c.subdimension === col.subdimension);
                    if (!exists) {
                        allColumns.push({
                            derecho: col.derecho,
                            dimension: col.dimension,
                            subdimension: col.subdimension,
                            evaluacion: ''
                        });
                    }
                });
            }
        });

        const headerDerechos = ["", ...allColumns.map(c => `Derecho: ${c.derecho}`)];
        const headerSubdimensiones = ["", ...allColumns.map(c => `Subdimension: ${c.subdimension}`)];

        const rows = [headerDerechos, headerSubdimensiones];

        structuralIndicators.forEach(indicator => {
            const { data, titulo } = indicator;
            const row: string[] = [escapeCSV(titulo)];

            if (data && Array.isArray(data) && data.length > 0) {
                const derechos = data as DerechoDto[];
                const indicatorColumns = buildStructuralColumns(derechos);

                allColumns.forEach(headerCol => {
                    const matchingCol = indicatorColumns.find(col => col.derecho === headerCol.derecho && col.subdimension === headerCol.subdimension);
                    row.push(matchingCol?.evaluacion ?? '');
                });
            } else {
                allColumns.forEach(() => row.push(''));
            }

            rows.push(row);
        });

        await addToZip('indicadores_estructurales', rows);
    }

    if (processResultIndicators.length) {
        const headerProcResult = ['Año'];
        const periodYears: string[] = [];

        if (periods.length > 0) {
            periods.forEach((p) => {
                const year = p.value.replace('Período', '').trim();
                periodYears.push(year);
            });
        }

        const indicatorColumns: {
            indicatorCode: string;
            encabezados: string[];
            data: { anio: string | null; encabezado?: string; valor: string }[];
            evaluations: Record<string, string | null> | undefined;
            hasEvaluations: boolean;
        }[] = [];

        const indicatorDictionaryColumns: {
            indicatorCode: string;
            name: string;
            description: string;
            source: string;
        }[] = [];

        for (const indicator of processResultIndicators) {
            const { breadcrumbs, tipo_indicador, data, id, maps } = indicator;
            const partsBreadcrumb = breadcrumbs.split('/').map(p => p.trim());

            const right = partsBreadcrumb[0].substring(0, 3).toUpperCase();
            const tipoLetra = tipo_indicador.charAt(0).toUpperCase();
            const indicatorCode = `${right}-${tipoLetra}-${id}`;

            indicatorDictionaryColumns.push({
                indicatorCode,
                name: indicator.titulo,
                description: indicator.descripcion,
                source: indicator.fuente
            });

            if (data && Array.isArray(data) && data.length > 0) {
                const datasets = data as DataGraphicsResultAndProcess[];

                const encabezadosUnicos = Array.from(
                    new Set(datasets.map(d => d.encabezado))
                );

                const hasEvaluations = !!indicator.evaluaciones;

                indicatorColumns.push({
                    indicatorCode,
                    encabezados: encabezadosUnicos,
                    data: datasets,
                    evaluations: indicator.evaluaciones,
                    hasEvaluations
                });

                encabezadosUnicos.forEach((encabezado) => {
                    const header = `${indicatorCode}. ${encabezado}`;
                    headerProcResult.push(header);
                });

                if (hasEvaluations) {
                    headerProcResult.push(`${indicatorCode}. Evaluaciones`);
                }
            }

            if (maps && maps.length > 0) {
                const heatMapsConEncabezado = maps.filter(
                    (map) => map.type === 'heat' && (map.data as HeatMapData[]).some((h) => !!h.encabezado)
                );

                heatMapsConEncabezado.forEach((map, mapIndex) => {
                    const heatData = map.data as HeatMapData[];
                    const encabezadosUnicos = Array.from(
                        new Set(heatData.filter((h) => !!h.encabezado).map((h) => h.encabezado as string))
                    );

                    const mapLabel = heatMapsConEncabezado.length > 0 ? ` ${mapIndex + 1}` : '';

                    indicatorColumns.push({
                        indicatorCode,
                        encabezados: encabezadosUnicos,
                        data: heatData,
                        evaluations: undefined,
                        hasEvaluations: false
                    });

                    encabezadosUnicos.forEach((encabezado) => {
                        headerProcResult.push(`${indicatorCode}. Mapa de calor${mapLabel}. ${encabezado}`);
                    });
                });
            }
        }

        const rows: string[][] = [headerProcResult];

        periodYears.forEach((year) => {
            const row = [year];

            indicatorColumns.forEach(({ encabezados, data, evaluations, hasEvaluations }) => {
                encabezados.forEach((encabezado) => {
                    const registro = data.find(d => d.anio === year && d.encabezado === encabezado);
                    row.push(registro?.valor?.toString() ?? '');
                });

                if (hasEvaluations) {
                    const evaluacion = evaluations?.[year] ?? ' ';
                    row.push(evaluacion);
                }
            });

            rows.push(row);
        });

        await addToZip('indicadores_proceso_resultado', rows);

        const rowsDictionary: string[][] = [
            headerDictionary,
            ...indicatorDictionaryColumns.map(ind => [
                ind.indicatorCode,
                ind.name,
                ind.description,
                ind.source
            ])
        ];
        await addToZip('diccionario', rowsDictionary);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, 'base_agrupada.zip');
}